import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export type PeriodType = 'years' | 'year' | 'month' | 'week' | 'custom';

type CustomRange = {
  from: string;
  to: string;
};

type PeriodFilterProps = {
  value: PeriodType;
  onChange: (value: PeriodType) => void;
  range: CustomRange;
  onRangeChange: (range: CustomRange) => void;
  sx?: object;
};

const options: { value: Exclude<PeriodType, 'custom'>; label: string }[] = [
  { value: 'years', label: 'Годы' },
  { value: 'year', label: 'Год' },
  { value: 'month', label: 'Месяц' },
  { value: 'week', label: 'Неделя' },
];

export default function PeriodFilter({
  value,
  onChange,
  range,
  onRangeChange,
  sx,
}: PeriodFilterProps) {
  const [open, setOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(range.from);
  const [draftTo, setDraftTo] = React.useState(range.to);

  React.useEffect(() => {
    setDraftFrom(range.from);
    setDraftTo(range.to);
  }, [range.from, range.to]);

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newValue: PeriodType | null) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setDraftFrom(range.from);
    setDraftTo(range.to);
    setOpen(false);
  };

  const handleApply = () => {
    if (!draftFrom || !draftTo) return;
    if (new Date(draftFrom) > new Date(draftTo)) return;

    onRangeChange({
      from: draftFrom,
      to: draftTo,
    });
    onChange('custom');
    setOpen(false);
  };

  const customLabel = range.from && range.to ? `${range.from} — ${range.to}` : 'Выбрать период';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', ...sx }}>
        <ToggleButtonGroup
          value={value === 'custom' ? null : value}
          exclusive
          onChange={handlePeriodChange}
        >
          {options.map(option => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Выбрать период вручную">
          <IconButton
            onClick={handleOpen}
            sx={{
              height: 48,
              width: 48,
              border: '1px solid',
              borderColor: 'grey.400',
              color: 'grey.600',
              borderRadius: 1,
              '&:hover': {
                borderColor: 'grey.500',
                backgroundColor: 'grey.100',
              },
            }}
          >
            <CalendarMonthIcon />
          </IconButton>
        </Tooltip>
        {value === 'custom' && range.from && range.to ? (
          <Typography variant="body2">{customLabel}</Typography>
        ) : null}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Выбрать период</DialogTitle>

        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '10px !important' }}
        >
          <TextField
            label="С"
            type="date"
            value={draftFrom}
            onChange={e => setDraftFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            label="По"
            type="date"
            value={draftTo}
            onChange={e => setDraftTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Отмена</Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={!draftFrom || !draftTo || new Date(draftFrom) > new Date(draftTo)}
          >
            Применить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
